/**
 * Zod validation schemas for inventory form types
 *
 * Design principles:
 * - Lenient validation: All fields optional (lawyers can add partial entries)
 * - Australian-specific: Postcode (4 digits), states, phone formats
 * - User-friendly error messages
 * - Runtime type safety with TypeScript inference
 *
 * Usage with React Hook Form:
 * ```tsx
 * const form = useForm({
 *   defaultValues: item,
 *   resolver: zodResolver(bankAccountSchema),
 *   mode: 'onChange'
 * });
 * ```
 */

import { z } from 'zod';

import { validateTitleReference } from '@/lib/constants/title-reference';

// ========================================
// Common Schemas & Utilities
// ========================================

/**
 * Institution schema - supports both object and string formats
 * Backend can return type as either a string or an InstitutionType object
 */
export const institutionSchema = z
  .union([
    z.object({
      id: z.number(),
      name: z.string(),
      // Accept any type format. The institution `type` arrives in several shapes
      // depending on the source: a plain slug string, the InstitutionType entity
      // ({ id, name, slug, ... }), or the AI-Scribe snapshot's type-config object
      // ({ name, slug, isNotifiable, ... }) which carries NO numeric id. Pinning
      // this to a strict `{ id, name, slug }` object wrongly rejected the latter,
      // surfacing a bogus "Invalid input" on otherwise-valid share registries.
      type: z.any().optional(),
      faviconUrl: z.string().nullable().optional(),
      // A freshly-added custom institution is the sentinel `{ id: -1, isCustom:
      // true, name, email }` (see InstitutionSearchField). Without declaring
      // `email`/`isCustom` here, zodResolver silently stripped them on submit
      // (z.object() drops unrecognised keys), so resolveInstitutionUpdateFields
      // never saw the email and treated the add-new-facility save as a no-op —
      // the item silently kept its old facility (LF-5339).
      //
      // `.nullable()` (not just `.optional()`): the backend column is nullable
      // and many institutions genuinely have no email on file — notably a bulk
      // of cryptocurrency-exchange rows. `.optional()` alone only widens to
      // `undefined`, so picking one of those institutions for any
      // institution-search field failed with "Expected string, received null"
      // and blocked save (LF-5845).
      email: z.string().nullable().optional(),
      isCustom: z.boolean().optional()
    }),
    z.string(),
    z.null()
  ])
  .optional();

/**
 * Australian state codes — the canonical set. `auStateSchema` enforces them on
 * fields that are always Australian (e.g. a vehicle's state of registration).
 * Address fields that can be overseas use {@link addressStateSchema} +
 * {@link refineAddressState} so a foreign region is accepted.
 */
export const AU_STATE_CODES = [
  'NSW',
  'VIC',
  'QLD',
  'SA',
  'WA',
  'TAS',
  'NT',
  'ACT'
] as const;

/**
 * Australian states. Use for fields that are always Australian. For an address
 * whose country can be non-Australian, use {@link addressStateSchema} together
 * with {@link refineAddressState} instead, so overseas regions are not rejected.
 */
export const auStateSchema = z
  .enum(AU_STATE_CODES)
  .optional()
  .or(z.literal(''));

/**
 * State / region field for an address that also carries a `country`. Stored as
 * free text so an overseas region (e.g. "Bay of Plenty", NZ) can be captured;
 * the Australian-state enum is enforced separately — and only for Australian
 * addresses — by {@link refineAddressState} on the owning object.
 */
export const addressStateSchema = z.string().optional().or(z.literal(''));

/**
 * Every spelling of Australia that an address `country` is known to hold. The field is free
 * text, so the same country arrives as the display name, the ISO 3166-1 alpha-2 code, or the
 * alpha-3 code — `AUS` is in fact the dominant stored value. Missing one of these would
 * silently exempt an Australian address from the state-code check.
 *
 * Kept in exact lock-step with the backend's `AUSTRALIA_ALIASES`
 * (`backend/src/common/dto/validators/australian-state-when-australian.validator.ts`).
 */
const AUSTRALIA_ALIASES = new Set(['australia', 'au', 'aus']);

/**
 * An address is treated as Australian when its country is blank/unset (the
 * product's default jurisdiction) or is any known spelling of Australia. Any
 * other value (e.g. "New Zealand") is overseas, so the AU state enum must not apply.
 */
export function isAustralianAddress(country?: string | null): boolean {
  const normalised = country?.trim().toLowerCase();
  return !normalised || AUSTRALIA_ALIASES.has(normalised);
}

/**
 * Cross-field validation for an address `{ state, country }`: an Australian
 * address must use a valid AU state code; an overseas address accepts any
 * free-text region. A blank/absent state is always allowed (lenient — partial
 * entries are permitted). Pairs with {@link addressStateSchema} at the field
 * level via the owning object's `superRefine`.
 */
export function refineAddressState(
  info: { state?: string | null; country?: string | null },
  ctx: z.RefinementCtx
): void {
  const state = info.state?.trim();
  if (
    state &&
    isAustralianAddress(info.country) &&
    !AU_STATE_CODES.includes(state as (typeof AU_STATE_CODES)[number])
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['state'],
      message: `State must be one of ${AU_STATE_CODES.join(', ')} for an Australian address`
    });
  }
}

/**
 * Strips whitespace and dash separators from a document-sourced identifier
 * before it's validated against its strict digit/format pattern. AI
 * extraction carries through whatever presentational formatting the source
 * document used (e.g. a bank statement's `31-530-1678`, a share registry's
 * `X 1234567890`) — that formatting is not part of the identifier itself, so
 * it's normalised away before the pattern check rather than rejecting an
 * otherwise-valid value for cosmetic reasons (LF-6201).
 *
 * Exported so a surface that re-declares one of these identifiers outside a
 * `z.preprocess` pipeline (e.g. a non-Zod editor's own `validate`) can still
 * bind to this rule instead of restating it (VALE-111).
 */
export function stripFormattingSeparators(val: unknown): unknown {
  return typeof val === 'string' ? val.replace(/[\s-]/g, '') : val;
}

/**
 * BSB format validation - 6 digits.
 *
 * Accepts the common Australian dash/space-separated formats (e.g. `123-456`,
 * `123 456`) by stripping separators before validation. The normalised,
 * digits-only value is what the schema outputs, so the value saved to the
 * backend always matches its own `^\d{6}$` constraint regardless of how the
 * lawyer typed it.
 */
export const bsbSchema = z.preprocess(
  stripFormattingSeparators,
  z
    .string()
    .regex(/^\d{6}$/, 'BSB must be 6 digits (e.g., 123-456)')
    .optional()
    .or(z.literal(''))
);

/**
 * Account number validation - 6-10 digits.
 *
 * Strips separators before validation (see {@link stripFormattingSeparators})
 * so an AI-extracted value like `31-530-1678` is accepted and normalised to
 * `315301678`, rather than rejected for carrying the source statement's
 * formatting (LF-6201).
 */
export const accountNumberSchema = z.preprocess(
  stripFormattingSeparators,
  z
    .string()
    .regex(/^\d{6,10}$/, 'Account number must be 6-10 digits')
    .optional()
    .or(z.literal(''))
);

/**
 * HIN format validation - X followed by 10 digits. Strips separators before
 * validation (see {@link stripFormattingSeparators}) for the same reason as
 * {@link accountNumberSchema} (LF-6201).
 */
export const hinSchema = z.preprocess(
  stripFormattingSeparators,
  z
    .string()
    .regex(
      /^X\d{10}$/,
      'HIN must be X followed by 10 digits (e.g., X1234567890)'
    )
    .optional()
    .or(z.literal(''))
);

/**
 * SRN format validation - I followed by 10 digits. Strips separators before
 * validation (see {@link stripFormattingSeparators}) for the same reason as
 * {@link accountNumberSchema} (LF-6201).
 */
export const srnSchema = z.preprocess(
  stripFormattingSeparators,
  z
    .string()
    .regex(
      /^I\d{10}$/,
      'SRN must be I followed by 10 digits (e.g., I1234567890)'
    )
    .optional()
    .or(z.literal(''))
);

/**
 * Positive number schema (for values, balances, prices)
 *
 * Uses z.preprocess to coerce null → undefined BEFORE type validation.
 * AI extraction may return null for empty numeric fields; without preprocessing,
 * z.number() rejects null before the old .transform() could convert it.
 */
export const positiveNumberSchema = z.preprocess(
  (val) => (val === null ? undefined : val),
  z.number().min(0, 'Value must be positive').optional()
);

/**
 * Signed number schema — same null-coercion as {@link positiveNumberSchema}
 * but with no floor. Used for liability `value`/`amountOwed`/`currentBalance`
 * fields, which may legitimately go negative: a "credit" balance where the
 * institution owes the estate money (e.g. a credit card refund, an ATO tax
 * credit) rather than the estate owing the institution (LF-5563). Not used
 * for asset value fields, which have no equivalent credit concept.
 */
export const signedNumberSchema = z.preprocess(
  (val) => (val === null ? undefined : val),
  z.number().optional()
);

/**
 * Positive integer schema (for shares, quantities)
 */
export const positiveIntegerSchema = z.preprocess(
  (val) => (val === null ? undefined : val),
  z
    .number()
    .int('Must be a whole number')
    .min(1, 'Must be at least 1')
    .optional()
);

/**
 * Reimbursement ref used during AI Matter Creation, before contacts have DB IDs.
 * Apply-changes on the BE resolves `contactIndex` → real `payeeContactId`.
 * `nonnegative()` (not `positive()`) is intentional: the first contact's
 * synthetic index is `0`.
 */
export const paidByContactRefSchema = z
  .object({
    contactIndex: z.number().int().nonnegative(),
    name: z.string().optional()
  })
  .nullable()
  .optional();

// ========================================
// Base Inventory Item Schema
// ========================================

/**
 * Base schema for all inventory items
 */
export const baseInventoryItemSchema = z.object({
  id: z.number().optional(),
  type: z.any().optional(), // Accept any type format (string, object, or partial object)
  value: positiveNumberSchema,
  status: z
    .enum([
      'draft',
      'identified',
      'notified',
      'not-held',
      'collected',
      'resolved',
      'confirmed',
      'settled',
      'not-owed',
      'archived'
    ])
    .optional(),
  isNonEstate: z.boolean().optional(),
  institution: institutionSchema,
  payeeContactId: z.number().int().positive().nullable().optional(),
  linkedInventoryItemId: z.number().int().positive().nullable().optional(),
  estateReference: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? '') // Transform null/undefined to empty string
});

// ========================================
// Bank Account Schema
// ========================================

export const bankAccountSchema = baseInventoryItemSchema.extend({
  institution: institutionSchema, // Institution optional (LF-4290) — recordable before it's known
  itemInfo: z
    .object({
      // The account holder's name as the bank has it (VALE-1940).
      accountName: z.string().optional(),
      bsb: bsbSchema,
      accountNumber: accountNumberSchema,
      // Date of Death Value — the stated balance plus interest accrued to the date of
      // death (VALE-802). Stays directly settable; the two components reconcile to it.
      valueAtDateOfDeath: positiveNumberSchema,
      // Unsigned like every other bank asset figure: debit interest on an overdrawn
      // account is a liability, not a negative bank asset.
      statedBalanceAtDateOfDeath: positiveNumberSchema,
      accruedInterestAtDateOfDeath: positiveNumberSchema,
      currentBalance: positiveNumberSchema,
      notes: z.string().optional()
    })
    .optional()
});

// ========================================
// Real Estate Schema
// ========================================

/**
 * Property instruction fields — preserved through form save for AI review
 * and the property lifecycle workflow (instruction, ownership, transfer details).
 */
const propertyInstructionFields = {
  ownershipType: z
    .enum(['solely-held', 'joint-tenants', 'tenants-in-common'])
    .nullable()
    .optional(),
  percentageOwned: z.number().min(1).max(99).nullable().optional(),
  instruction: z
    .enum(['sell', 'transfer-to-beneficiary', 'notice-of-death', 'undecided'])
    .nullable()
    .optional(),
  transferSubFlow: z
    .enum(['direct-to-beneficiary', 'via-executor'])
    .nullable()
    .optional(),
  transferBeneficiaries: z
    .array(
      z.object({
        contactId: z.number(),
        contact: z
          .object({
            id: z.number(),
            firstName: z.string(),
            lastName: z.string()
          })
          .optional(),
        percentage: z.number().optional()
      })
    )
    .nullable()
    .optional(),
  beneficiaryHoldingType: z
    .enum(['joint-tenants', 'tenants-in-common'])
    .nullable()
    .optional(),
  hasContraryIntention: z.boolean().nullable().optional(),
  propertyType: z
    .enum(['residential', 'commercial', 'rural', 'vacant-land'])
    .nullable()
    .optional(),
  isPrimaryResidence: z.boolean().nullable().optional(),
  milestoneSummary: z.any().optional(),
  realisedValue: z.number().nullable().optional()
};

export const realEstateSchema = baseInventoryItemSchema.extend({
  itemInfo: z
    .object({
      streetAddress: z.string().optional(),
      suburb: z.string().optional(),
      // Free text + country-aware enum check below — an overseas region (e.g.
      // "Bay of Plenty") must be capturable when the country isn't Australia.
      state: addressStateSchema,
      postcode: z.string().optional().or(z.literal('')), // Accept any postcode format without validation
      country: z.string().optional(),
      folioIdentifier: z.string().optional(),
      estateType: z
        .enum([
          'fee-simple',
          'leasehold',
          'crown',
          'life-estate',
          'easement-only',
          'other'
        ])
        .nullable()
        .optional(),
      valueAtDateOfDeath: positiveNumberSchema,
      currentValue: positiveNumberSchema,
      notes: z.string().optional(),
      ...propertyInstructionFields
    })
    // LF-4071: validate the title reference against its jurisdiction's
    // expected format. Keyed off the property's own state (the law-firm
    // fallback is applied at the field/preview layer where it's available).
    // Lenient — a missing reference or unknown state is accepted.
    .superRefine((info, ctx) => {
      // Enforce the AU state enum only for Australian addresses; overseas
      // regions are free text (LF-4549).
      refineAddressState(info, ctx);
      const message = validateTitleReference(info.folioIdentifier, info.state);
      if (message) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['folioIdentifier'],
          message
        });
      }
    })
    .optional()
});

// ========================================
// Aged Care Schema
// ========================================

export const agedCareSchema = baseInventoryItemSchema.extend({
  institution: institutionSchema, // Institution optional (LF-4290) — recordable before it's known
  itemInfo: z
    .object({
      facility: z.string().optional(), // Legacy field
      valueAtDateOfDeath: positiveNumberSchema, // RAD Amount (principal)
      // Refund due — principal + credit + interest, less fees and retention withheld
      // (LF-6210). Stays directly settable; the components below reconcile to it.
      currentValue: positiveNumberSchema,
      heldCredit: positiveNumberSchema,
      accruedInterest: positiveNumberSchema,
      feesDeducted: positiveNumberSchema,
      retentionDeducted: positiveNumberSchema,
      notes: z.string().optional()
    })
    .optional()
});

// ========================================
// Superannuation Schema
// ========================================

export const superannuationSchema = baseInventoryItemSchema.extend({
  institution: institutionSchema, // Institution optional (LF-4290) — recordable before it's known
  itemInfo: z
    .object({
      superannuation: z.string().optional(), // Legacy field
      memberNumber: z.string().optional(),
      valueAtDateOfDeath: positiveNumberSchema,
      currentValue: positiveNumberSchema,
      notes: z.string().optional()
    })
    .optional()
});

// ========================================
// Shares Schema (SRN)
// ========================================

/**
 * Share instruction fields — shared by SRN and HIN schemas
 */
const shareInstructionFields = {
  ownershipType: z
    .enum(['solely-held', 'joint-tenants', 'not-held'])
    .nullable()
    .optional(),
  instruction: z
    .enum([
      'sell',
      'transfer-to-beneficiary',
      'surviving-joint-holder',
      'undecided'
    ])
    .nullable()
    .optional(),
  transferBeneficiaries: z
    .array(
      z.object({
        contactId: z.number(),
        contact: z
          .object({
            id: z.number(),
            firstName: z.string(),
            lastName: z.string()
          })
          .optional(),
        units: z.number()
      })
    )
    .nullable()
    .optional(),
  realisedValue: z.number().nullable().optional()
};

export const sharesSchema = baseInventoryItemSchema.extend({
  institution: institutionSchema, // Optional — AI Scribe may capture an SRN share before the registry is known (LF-3606)
  itemInfo: z
    .object({
      company: z.any().optional(), // Can be SrnShare object, CustomSrnShare, or string
      ticker: z.string().optional(), // Stored separately for read mode
      companyName: z.string().optional(), // Stored separately for read mode
      shareRegistry: z.string().optional(),
      srn: srnSchema,
      numberOfShares: z.number().optional(),
      sharePriceAtDateOfDeath: positiveNumberSchema,
      valueAtDateOfDeath: positiveNumberSchema,
      currentPrice: positiveNumberSchema,
      currentValue: positiveNumberSchema,
      notes: z.string().optional(),
      ...shareInstructionFields
    })
    .optional()
});

// ========================================
// HIN Portfolio Schema (with holdings array)
// ========================================

/**
 * Individual holding within HIN portfolio
 */
export const holdingSchema = z.object({
  tickerCompany: z.string().optional(), // Legacy format (deprecated)
  ticker: z.string().optional(), // New format - separate ticker
  companyName: z.string().optional(), // New format - separate company name
  numberOfShares: z.number().optional(),
  sharePriceAtDateOfDeath: positiveNumberSchema,
  valueAtDateOfDeath: positiveNumberSchema,
  currentPrice: positiveNumberSchema,
  currentValue: positiveNumberSchema
});

export const hinPortfolioSchema = baseInventoryItemSchema.extend({
  institution: z.any().optional(), // EXCEPTION: Institution optional for HIN shares per user requirement
  itemInfo: z
    .object({
      broker: z.string().optional(), // Legacy field - migrating to institution
      hin: hinSchema,
      stock: z.any().optional(), // Internal form state - stores selected stock object (TickerSearchResult or CustomHinShare)
      portfolio: z.array(holdingSchema).optional(),
      valueAtDateOfDeath: positiveNumberSchema,
      currentValue: positiveNumberSchema,
      notes: z.string().optional(),
      ...shareInstructionFields
    })
    .optional()
});

// ========================================
// Life Insurance Schema
// ========================================

export const lifeInsuranceSchema = baseInventoryItemSchema.extend({
  institution: institutionSchema, // Institution optional (LF-4290) — recordable before it's known
  itemInfo: z
    .object({
      insuranceCompany: z.string().optional(), // Legacy field
      nameOfPolicy: z.string().optional(),
      policyNumber: z.string().optional(),
      valueAtDateOfDeath: positiveNumberSchema,
      currentValue: positiveNumberSchema,
      notes: z.string().optional()
    })
    .optional()
});

// ========================================
// Cryptocurrency Schema
// ========================================

export const cryptocurrencySchema = baseInventoryItemSchema.extend({
  institution: institutionSchema, // Institution optional (LF-4290) — recordable before it's known
  itemInfo: z
    .object({
      cryptocurrencyExchangeName: z.string().optional(),
      walletAddress: z.string().optional(),
      portfolio: z
        .array(
          z.object({
            coinName: z.string().optional(),
            quantity: positiveNumberSchema,
            sharePriceAtDateOfDeath: positiveNumberSchema,
            valueAtDateOfDeath: positiveNumberSchema,
            currentPrice: positiveNumberSchema,
            currentValue: positiveNumberSchema
          })
        )
        .optional(),
      valueAtDateOfDeath: positiveNumberSchema,
      currentValue: positiveNumberSchema,
      notes: z.string().optional()
    })
    .optional()
});

// ========================================
// Vehicle Schema
// ========================================

/**
 * Vehicle instruction fields — preserved through form save for AI review
 * and the vehicle lifecycle workflow. Vehicles transfer jointly with no
 * per-beneficiary percentage (chattels are indivisible at the registry).
 */
const vehicleInstructionFields = {
  instruction: z
    .enum(['sell', 'transfer-to-beneficiary', 'scrap-or-dispose', 'undecided'])
    .nullable()
    .optional(),
  transferBeneficiaries: z
    .array(
      z.object({
        contactId: z.number(),
        contact: z
          .object({
            id: z.number(),
            firstName: z.string(),
            lastName: z.string()
          })
          .optional()
      })
    )
    .nullable()
    .optional(),
  hasContraryIntention: z.boolean().nullable().optional(),
  milestoneSummary: z.any().optional()
};

export const vehicleSchema = baseInventoryItemSchema.extend({
  itemInfo: z
    .object({
      vehicleType: z.string().optional(), // Car, Motorcycle, Boat, etc.
      make: z.string().optional(),
      model: z.string().optional(),
      year: z
        .number()
        .int()
        .min(1900)
        .max(new Date().getFullYear() + 1)
        .optional(),
      registrationNumber: z.string().optional(),
      registeredState: auStateSchema,
      valueAtDateOfDeath: positiveNumberSchema,
      currentValue: positiveNumberSchema,
      notes: z.string().optional(),
      ...vehicleInstructionFields
    })
    .optional()
});

// ========================================
// Other Assets Schema
// ========================================

export const otherSchema = baseInventoryItemSchema.extend({
  // Use lenient schema (z.any()) to preserve custom institution properties (email, isCustom)
  // Institution is optional for "other" type
  institution: z.any().optional(),
  itemInfo: z
    .object({
      description: z.string().optional(),
      // Legacy fields - kept for backward compatibility with existing data
      hasOrganisation: z.boolean().optional(),
      institutionName: z.string().optional(),
      emailAddress: z
        .string()
        .email('Invalid email format')
        .optional()
        .or(z.literal('')),
      phoneNumber: z.string().optional(),
      valueAtDateOfDeath: positiveNumberSchema,
      currentBalance: positiveNumberSchema,
      notes: z.string().optional(),
      // Edited on the Redemption tab and, since VALE-1940, on the record.
      instruction: z
        .enum(['collect-in-trust', 'transfer-to-beneficiary', 'undecided'])
        .nullable()
        .optional()
    })
    .optional()
});

// ========================================
// Employment Entitlements Schema
// ========================================

/** Unpaid wages, leave and similar owed by the employer (VALE-1940). */
export const employmentEntitlementsSchema = baseInventoryItemSchema.extend({
  institution: z.any().optional(),
  itemInfo: z
    .object({
      description: z.string().optional(),
      valueAtDateOfDeath: positiveNumberSchema,
      currentBalance: positiveNumberSchema,
      notes: z.string().optional()
    })
    .optional()
});

// ========================================
// Managed Funds Schema
// ========================================

export const managedFundsSchema = baseInventoryItemSchema.extend({
  institution: institutionSchema, // Institution optional (LF-4290) — recordable before it's known
  itemInfo: z
    .object({
      memberNumber: z.string().optional(),
      valueAtDateOfDeath: positiveNumberSchema,
      currentValue: positiveNumberSchema,
      notes: z.string().optional()
    })
    .optional()
});

// ========================================
// Private Company Schema
// ========================================

export const privateCompanySchema = baseInventoryItemSchema.extend({
  institution: z.any().optional(), // No institution — held directly by the deceased
  itemInfo: z
    .object({
      companyName: z.string().optional(),
      acn: z.string().optional(),
      abn: z.string().optional(),
      numberOfShares: positiveNumberSchema,
      valueAtDateOfDeath: positiveNumberSchema,
      currentValue: positiveNumberSchema,
      ownershipType: z
        .enum(['solely-held', 'joint-tenants', 'tenants-in-common'])
        .optional(),
      notes: z.string().optional()
    })
    .optional()
});

// ========================================
// Funeral & Burial Schema
// ========================================

export const funeralBurialSchema = baseInventoryItemSchema.extend({
  value: signedNumberSchema,
  institution: z.any().optional(),
  itemInfo: z
    .object({
      description: z.string().optional(),
      serviceDate: z.string().optional(),
      amountOwed: signedNumberSchema,
      currentBalance: signedNumberSchema,
      notes: z.string().optional(),
      paidByContactRef: paidByContactRefSchema
    })
    .optional()
});

// ========================================
// Legal & Professional Schema
// ========================================

export const legalProfessionalSchema = baseInventoryItemSchema.extend({
  value: signedNumberSchema,
  institution: z.any().optional(),
  itemInfo: z
    .object({
      description: z.string().optional(),
      invoiceReference: z.string().optional(),
      serviceType: z.string().optional(),
      amountOwed: signedNumberSchema,
      currentBalance: signedNumberSchema,
      notes: z.string().optional(),
      paidByContactRef: paidByContactRefSchema
    })
    .optional()
});

// ========================================
// Mortgage Schema
// ========================================

export const mortgageSchema = baseInventoryItemSchema.extend({
  value: signedNumberSchema,
  institution: z.any().optional(),
  itemInfo: z
    .object({
      description: z.string().optional(),
      streetAddress: z.string().optional(),
      suburb: z.string().optional(),
      // Free text + country-aware enum check (see realEstateSchema / LF-4549) —
      // a mortgage may secure an overseas property.
      state: addressStateSchema,
      postcode: z.string().optional().or(z.literal('')), // Accept any postcode format without validation
      country: z.string().optional(),
      accountNumber: z.string().optional(),
      interestRate: z.number().min(0).optional(),
      repaymentAmount: positiveNumberSchema,
      repaymentFrequency: z
        .enum(['weekly', 'fortnightly', 'monthly'])
        .optional(),
      encumbranceType: z
        .enum(['first-mortgage', 'second-mortgage', 'charge', 'lien'])
        .optional(),
      registrationNumber: z.string().optional(),
      registrationDate: z.string().optional(),
      ownershipType: z
        .enum(['solely-held', 'joint-tenants', 'tenants-in-common'])
        .optional(),
      resolutionPlan: z
        .enum([
          'property-sale',
          'beneficiary-refinances',
          'joint-tenant-assumes',
          'estate-pays',
          'undecided'
        ])
        .optional(),
      amountOwed: signedNumberSchema,
      currentBalance: signedNumberSchema,
      notes: z.string().optional(),
      paidByContactRef: paidByContactRefSchema
    })
    // Enforce the AU state enum only for Australian addresses (LF-4549).
    .superRefine(refineAddressState)
    .optional()
});

// ========================================
// Vehicle Loan Schema
// ========================================

export const vehicleLoanSchema = baseInventoryItemSchema.extend({
  value: signedNumberSchema,
  institution: z.any().optional(),
  itemInfo: z
    .object({
      description: z.string().optional(),
      accountNumber: z.string().optional(),
      ownershipType: z
        .enum(['solely-held', 'joint-tenants', 'tenants-in-common'])
        .optional(),
      resolutionPlan: z
        .enum([
          'vehicle-sale',
          'beneficiary-assumes',
          'estate-pays',
          'undecided'
        ])
        .optional(),
      amountOwed: signedNumberSchema,
      currentBalance: signedNumberSchema,
      notes: z.string().optional(),
      paidByContactRef: paidByContactRefSchema
    })
    .optional()
});

// ========================================
// Tax Liability Schema
// ========================================

export const taxLiabilitySchema = baseInventoryItemSchema.extend({
  value: signedNumberSchema,
  institution: z.any().optional(),
  itemInfo: z
    .object({
      description: z.string().optional(),
      taxType: z
        .enum([
          'income-tax',
          'capital-gains-tax',
          'tax-return',
          'land-tax',
          'council-rates',
          'water-rates',
          'other'
        ])
        .optional(),
      taxPeriod: z.string().optional(),
      lodgementDate: z.string().optional(),
      // The property a land tax or rate is levied on (VALE-1387, row VALE-1940).
      propertyAddress: z.string().optional(),
      amountOwed: signedNumberSchema,
      currentBalance: signedNumberSchema,
      notes: z.string().optional(),
      paidByContactRef: paidByContactRefSchema
    })
    .optional()
});

// ========================================
// Credit Card Schema
// ========================================

export const creditCardSchema = baseInventoryItemSchema.extend({
  value: signedNumberSchema,
  institution: z.any().optional(),
  itemInfo: z
    .object({
      description: z.string().optional(),
      cardType: z.string().optional(),
      // Strips separators before validation (LF-6201) — see stripFormattingSeparators.
      lastFourDigits: z.preprocess(
        stripFormattingSeparators,
        z
          .string()
          .regex(/^\d{4}$/, 'Must be exactly 4 digits')
          .optional()
          .or(z.literal(''))
      ),
      amountOwed: signedNumberSchema,
      currentBalance: signedNumberSchema,
      notes: z.string().optional(),
      paidByContactRef: paidByContactRefSchema
    })
    .optional()
});

// ========================================
// Personal Loan Schema
// ========================================

export const personalLoanSchema = baseInventoryItemSchema.extend({
  value: signedNumberSchema,
  institution: z.any().optional(),
  itemInfo: z
    .object({
      description: z.string().optional(),
      accountNumber: z.string().optional(),
      loanType: z.string().optional(),
      amountOwed: signedNumberSchema,
      currentBalance: signedNumberSchema,
      notes: z.string().optional(),
      paidByContactRef: paidByContactRefSchema
    })
    .optional()
});

// ========================================
// Utility Bill Schema
// ========================================

export const utilityBillSchema = baseInventoryItemSchema.extend({
  value: signedNumberSchema,
  institution: z.any().optional(),
  itemInfo: z
    .object({
      description: z.string().optional(),
      billType: z
        .enum([
          'electricity',
          'gas',
          'water',
          'internet',
          'phone',
          'council-rates',
          'other'
        ])
        .optional(),
      accountNumber: z.string().optional(),
      /*
        The service address. `propertyAddress` is the pre-VALE-302 single
        string, kept so an existing bill still validates and so the attribute
        row can read it as a fallback; the structured parts are what that row
        writes from then on. Both are declared because `itemInfo` is a plain
        `z.object` — Zod strips what it does not know, so omitting the new keys
        would have the flag-off form silently discard a structured address on
        its next submit.
      */
      propertyAddress: z.string().optional(),
      streetAddress: z.string().optional(),
      suburb: z.string().optional(),
      state: z.string().optional(),
      postcode: z.string().optional(),
      country: z.string().optional(),
      amountOwed: signedNumberSchema,
      currentBalance: signedNumberSchema,
      notes: z.string().optional(),
      paidByContactRef: paidByContactRefSchema
    })
    .optional()
});

// ========================================
// Estate Expense Schema
// ========================================

export const estateExpenseSchema = baseInventoryItemSchema.extend({
  value: signedNumberSchema,
  institution: z.any().optional(),
  itemInfo: z
    .object({
      description: z.string().optional(),
      expenseType: z.string().optional(),
      amountOwed: signedNumberSchema,
      currentBalance: signedNumberSchema,
      notes: z.string().optional(),
      paidByContactRef: paidByContactRefSchema
    })
    .optional()
});

// ========================================
// Other Liability Schema
// ========================================

export const otherLiabilitySchema = baseInventoryItemSchema.extend({
  value: signedNumberSchema,
  institution: z.any().optional(),
  itemInfo: z
    .object({
      description: z.string().optional(),
      creditorType: z.enum(['person', 'entity']).optional(),
      amountOwed: signedNumberSchema,
      currentBalance: signedNumberSchema,
      notes: z.string().optional(),
      paidByContactRef: paidByContactRefSchema
    })
    .optional()
});

// ========================================
// Schema Selector Helper
// ========================================

/**
 * Get the appropriate schema for an inventory type
 */
export function getInventorySchema(type: string) {
  // The value type spells the INPUT side out as an object. A bare `z.ZodSchema` was
  // enough under zod 3, where its unparameterised input was `any`; zod 4 defaults it to
  // `unknown`, and `zodResolver` constrains its input to react-hook-form's `FieldValues`,
  // which `unknown` does not satisfy. The map is deliberately heterogeneous — one runtime
  // string picks one of ~24 schemas — so "some object schema" is the most precise this can be.
  const schemaMap: Record<
    string,
    z.ZodType<unknown, Record<string, unknown>>
  > = {
    // Asset types
    'bank-account': bankAccountSchema,
    'real-estate': realEstateSchema,
    'aged-care': agedCareSchema,
    'nursing-home-bond': agedCareSchema, // Alias for aged-care
    superannuation: superannuationSchema,
    shares: sharesSchema,
    'srn-shares': sharesSchema, // Alias for shares
    'hin-shares': hinPortfolioSchema, // Alias for hin-portfolio
    'hin-portfolio': hinPortfolioSchema,
    'life-insurance': lifeInsuranceSchema,
    cryptocurrency: cryptocurrencySchema,
    vehicle: vehicleSchema,
    'managed-funds': managedFundsSchema,
    'private-company': privateCompanySchema,
    other: otherSchema,
    'employment-entitlements': employmentEntitlementsSchema,
    // Liability types
    'funeral-burial': funeralBurialSchema,
    'legal-professional': legalProfessionalSchema,
    mortgage: mortgageSchema,
    'vehicle-loan': vehicleLoanSchema,
    'tax-liability': taxLiabilitySchema,
    'credit-card': creditCardSchema,
    'personal-loan': personalLoanSchema,
    'utility-bill': utilityBillSchema,
    'estate-expense': estateExpenseSchema,
    'other-liability': otherLiabilitySchema
  };

  return schemaMap[type] || baseInventoryItemSchema;
}

// ========================================
// Validation Helpers
// ========================================

/**
 * Validate inventory item data
 * Returns { success: true, data } or { success: false, error }
 */
export function validateInventoryItem(type: string, data: unknown) {
  try {
    const schema = getInventorySchema(type);
    const validated = schema.parse(data);
    return { success: true, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        error: error.issues[0]?.message || 'Validation failed',
        errors: error.issues
      };
    }
    return { success: false, error: 'Unknown validation error' };
  }
}

/**
 * Get validation error messages for a field
 */
export function getFieldErrors(
  errors: z.ZodError | undefined,
  fieldPath: string
): string[] {
  if (!errors) return [];

  return errors.issues
    .filter((err) => err.path.join('.') === fieldPath)
    .map((err) => err.message);
}
